/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2018. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { FormHelpersService } from 'app/shared/services';
import { Constants } from 'app/shared/models/constants.model';
import * as fromSharedStore from 'app/shared/store';

@Component({
  selector: '[addAthleteModal]',
  templateUrl: './add-athlete-modal.component.html',
  styleUrls: ['./add-athlete-modal.component.scss'],
})
export class AddAthleteModalComponent implements OnInit {
  athleteForm: FormGroup;
  EVENT_TYPES = Constants.EVENT_TYPES;
  USER_TYPES = Constants.USER_TYPE;

  @Input() event;
  @Output() closeModal: EventEmitter<any> = new EventEmitter();

  availableGradYears = [];
  showInvalidMessage = false;

  currentUser: any;
  private componentDestroyed: Subject<any> = new Subject();

  constructor(public formHelpersService: FormHelpersService, private sharedStore: Store<fromSharedStore.SharedState>) {}

  ngOnInit() {
    this.sharedStore
      .select(fromSharedStore.getUserData)
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((currentUser) => {
        this.currentUser = currentUser.userData;
      });

    this.availableGradYears = this.event.gradYears
      ? this.event.gradYears.map((gradYear) => ({ ...gradYear, selected: gradYear.selected || false }))
      : [];

    this.athleteForm = new FormGroup({
      gradYear: new FormControl('', [Validators.required]),
      fullName: new FormControl('', [Validators.required]),
      teamName: new FormControl('', []),
      email: new FormControl('', [
        Validators.required,
        Validators.pattern('^[a-zA-Z0-9._+-]{1,}@{1}[a-zA-Z0-9.-]{1,}[.]{1}[a-zA-Z+]{2,}'),
        this.currentUserEmail,
      ]),
    });
  }

  ngOnChanges() {
    if (!this.event.gradYears) {
      this.athleteForm.get('gradYear').clearValidators();
    } else {
      this.availableGradYears = this.event.gradYears
        ? this.event.gradYears.map((gradYear) => ({ ...gradYear, selected: gradYear.selected || false }))
        : [];
    }

    if (!this.event.isTeamPackage) {
      this.athleteForm.get('fullName').setValidators(Validators.required);
    } else {
      this.athleteForm.get('fullName').clearValidators();
    }

    if (this.event.event.eventType === this.EVENT_TYPES.TEAM || this.event.isTeamPackage) {
      this.athleteForm.get('teamName').setValidators(Validators.required);
    } else {
      this.athleteForm.get('teamName').clearValidators();
    }
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
    this.componentDestroyed.complete();
  }

  close() {
    this.closeModal.emit({ add: false });
  }

  setAthlete() {
    if (!this.event.gradYears) {
      this.athleteForm.get('gradYear').clearValidators();
      this.athleteForm.get('gradYear').updateValueAndValidity();
    }

    if (this.athleteForm.invalid) {
      this.formHelpersService.markFieldsAsDirty(this.athleteForm);
      this.showInvalidMessage = true;
      return;
    }
    const newAthlete = this.athleteForm.value;
    this.closeModal.emit({ add: true, value: newAthlete });
  }

  private currentUserEmail = (control: FormControl) => {
    if (this.currentUser && control.value === this.currentUser.email) {
      return { currentUserEmail: true };
    }
    return null;
  };
}
